import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import Image from 'next/image';
import { Box, DialogActions, DialogContent, DialogContentText, DialogTitle, Typography } from '@mui/material';

import useAlert from '../../../../hooks/useAlert';
import useMobileDetect from '../../../../hooks/useMobileDetect';
import usePoll from '../../../../hooks/usePoll';
import backend from '../../../../lib/backend';
import getStudentSponsorshipApplication from '../../../../lib/backend/requests/getStudentSponsorshipApplication';
import { approvedApplicationState } from '../../../../state/atoms/approvedApplication';
import { ProfileCompletionStepComponentBaseProps } from '../../../../types/ProfileCompletionStep';
import ProfilePopUpButtons from '../ProfilePopUpButtons';

import signAgreementGraphic from '../../../../public/icons/signed-doc.svg';

import { AxiosError } from 'axios';

type ProfileSignAgreementStepProps = ProfileCompletionStepComponentBaseProps;

const ProfileSignAgreementStep: FunctionComponent<
  ProfileSignAgreementStepProps
> = ({
  closeModal,
  description,
  navigatePrevious,
  sponsorshipApplication,
  title,
}) => {
  const [ loading, setLoading ] = useState(true);
  const { showErrorAlert } = useAlert();
  const isMobile = useMobileDetect();
  const setApprovedApplication = useSetRecoilState(approvedApplicationState);

  const { result: updatedApplication, isPolling } = usePoll(
    async () => {
      const res = await getStudentSponsorshipApplication({ id: sponsorshipApplication.id });

      return res.data;
    },
    2000,
    (app) => !!app?.envelope_id,
    60,
  );

  const handleSignAgreement = useCallback(async () => {
    setLoading(true);

    try {
      const {
        data: { sign_url },
      } = await backend.getSponsorshipAgreementSignLink(
        sponsorshipApplication.id,
      );
      window.location.href = sign_url;
    } catch (error) {
      const axiosError = error as AxiosError<string>;
      setLoading(false);
      showErrorAlert(axiosError.response?.data || 'Something went wrong.');
    }
  }, [ sponsorshipApplication, showErrorAlert ]);

  useEffect(() => {
    if (!isPolling) {
      setLoading(false);

      if (updatedApplication?.envelope_id) {
        setApprovedApplication(updatedApplication);
      }
    }
  }, [ isPolling, setApprovedApplication, updatedApplication ]);

  return (
    <Box sx={{ textAlign: 'center' }}>
      <Box
        sx={
          {
            position: 'relative',
            width: 200,
            height: 200,
            mx: 'auto',
            mb: 3,
          }
        }
      >
        <Image
          alt='Sign Agreement'
          layout='fill'
          objectFit='contain'
          src={signAgreementGraphic}
        />
      </Box>
      <DialogTitle id='sign-agreement-title'>
        <Typography align='center' variant={isMobile ? 'h3' : 'h2'}>
          {title}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ p: 0, mb: 4 }}>
        <DialogContentText id='sign-agreement-description'>
          <Typography align='center' variant={isMobile ? 'subtitle2' : 'subtitle1'}>
            {description}
          </Typography>
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ p: 0 }}>
        <ProfilePopUpButtons
          disabled={loading}
          loadingStandalone={loading}
          onBack={navigatePrevious}
          onSaveAndExit={closeModal}
          onStandaloneClick={handleSignAgreement}
          showBack={true}
          showSaveAndExit={true}
          standalone={true}
          standaloneText='Sign my agreement'
        />
      </DialogActions>
    </Box>
  );
};

export default ProfileSignAgreementStep;
